/**
 * CONSAI Business App - Starter Plan Thank You Page
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Calendar, CreditCard, Users, FileText, MessageSquare, BarChart3, Sparkles } from 'lucide-react';
import { trackSubscribe } from '../utils/metaPixel';

const ThankYouStarter = () => {
  const navigate = useNavigate();
  
  // Calculate next billing date (1 month from now)
  const nextBillingDate = new Date();
  nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
  const formattedDate = nextBillingDate.toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  // Fire Meta Pixel event on page load
  useEffect(() => {
    trackSubscribe('Starter Plan', 19);
  }, []);

  const features = [
    { icon: Users, text: 'Up to 50 customers' },
    { icon: FileText, text: '50 invoices/month' },
    { icon: MessageSquare, text: '10 AI questions/week' },
    { icon: Sparkles, text: 'Weekly AI summary' },
    { icon: BarChart3, text: 'Basic analytics' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_consai-foundation/artifacts/0mnri4ba_CONSAI%20Logo_compressed.png" 
            alt="CONSAI" 
            className="h-10 w-10"
          />
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-600">
            Go to Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#021945] font-manrope mb-4">
            Welcome to Starter Plan! 🎉
          </h1>
          <p className="text-lg text-slate-600">
            Your subscription is now active. Start managing your business smarter with CONSAI.
          </p>
        </div>

        {/* Plan Details Card */}
        <Card className="mb-8 border-2 border-blue-100">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 font-manrope mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              Plan Details
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Plan</span>
                <span className="font-semibold text-slate-900">Starter Plan</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Amount Paid</span>
                <span className="font-semibold text-green-600">₹19/month</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Billing</span>
                <span className="font-semibold text-slate-900">Monthly auto-renewal</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-slate-600 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Next billing
                </span>
                <span className="font-semibold text-slate-900">{formattedDate}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features Card */}
        <Card className="mb-8 bg-blue-50/50 border-blue-100">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 font-manrope mb-4">
              Your Starter Features
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <feature.icon className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-slate-700 text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA Button */}
        <div className="text-center">
          <Button 
            onClick={() => navigate('/dashboard')}
            className="bg-[#021945] hover:bg-[#021945]/90 text-white px-8 py-6 text-lg"
            data-testid="go-to-dashboard-btn"
          >
            Go to Dashboard
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-slate-500 text-sm">
        <p>© 2026 BUFFETISM IT VENTURES PVT LTD. All rights reserved</p>
        <p className="mt-2">Need help? Contact support@consai.in</p>
      </footer>
    </div>
  );
};

export default ThankYouStarter;
